import { t, te } from '@/i18n';
import AppMessages from '@/models/core/AppMessages';

/**
 * Extracts and translates error messages with fallback support
 * @param error The error object from Supabase or other sources
 * @param domain The error domain (e.g., 'employee', 'movement', 'auth')
 * @returns Translated error message or generic fallback
 */
export function getTranslatedError(error: unknown, domain: string): string {
  let errorCode: string;

  // Extract error code from different error types
  if (typeof error === 'string') {
    errorCode = error;
  } else if (error && typeof error === 'object' && 'message' in error) {
    errorCode = (error as { message: string }).message;
  } else if (error && typeof error === 'object' && 'code' in error) {
    errorCode = (error as { code: string }).code;
  } else {
    errorCode = 'UNKNOWN_ERROR';
  }

  // Try to find translation with domain prefix
  const domainKey = `errors.${domain}.${errorCode}`;
  if (te(domainKey)) {
    return t(domainKey);
  }

  // Try to find translation in general errors section
  const generalKey = `errors.${errorCode}`;
  if (te(generalKey)) {
    return t(generalKey);
  }

  // Try to find translation without any prefix (for legacy support)
  if (te(errorCode)) {
    return t(errorCode);
  }

  // Check if it's a known Supabase error pattern
  if (errorCode.includes('violates foreign key constraint')) {
    return t('errors.FOREIGN_KEY_VIOLATION');
  }

  if (errorCode.includes('duplicate key value')) {
    return t('errors.DUPLICATE_KEY_VIOLATION');
  }

  if (errorCode.includes('permission denied')) {
    return t('errors.PERMISSION_DENIED');
  }

  // Check for RLS policy violations specific to employee role changes
  if (errorCode.includes('new row violates row-level security policy') && errorCode.includes('employees')) {
    if (domain === 'employee') {
      return t('errors.employee.EMPLOYEE_UPDATE_FAILED_CANNOT_CHANGE_OWN_ROLE');
    }
    return t('errors.PERMISSION_DENIED');
  }

  // Return generic error message as fallback
  return t('errors.GENERIC_ERROR');
}

/**
 * Handles service errors consistently across the application
 * @param error The error object
 * @param domain The error domain
 * @param showMessage Whether to show the error message to the user (default: true)
 * @returns The translated error message
 */
export function handleServiceError(error: unknown, domain: string, showMessage: boolean = true): string {
  const translatedError = getTranslatedError(error, domain);

  if (showMessage) {
    AppMessages.error(translatedError);
  }

  return translatedError;
}

/**
 * Creates a standardized service response with error handling
 * @param data The response data
 * @param error The error object
 * @param domain The error domain
 * @returns ServiceResponse with translated error
 */
export function createServiceResponse<T>(data: T | null, error: unknown, domain: string): { data: T | null; error: string | null } {
  if (error) {
    const translatedError = handleServiceError(error, domain);
    return { data: null, error: translatedError };
  }

  return { data, error: null };
}

/**
 * Extracts error code from Supabase error messages for movement operations
 * This is a specialized function for movement errors that have complex patterns
 * @param errorMessage The raw error message from Supabase
 * @returns A standardized error code
 */
export function extractMovementErrorCode(errorMessage: string): string {
  // Check if the error message directly contains a known error code
  if (te(`errors.movement.${errorMessage}`)) {
    return errorMessage;
  }

  // For legacy foreign key violations
  if (errorMessage.includes('violates foreign key constraint')) {
    if (errorMessage.includes('fk_movements_employees')) {
      return 'MOVEMENT_CREATION_FAILED_INVALID_EMPLOYEE';
    } else if (errorMessage.includes('fk_movements_foremans')) {
      return 'MOVEMENT_CREATION_FAILED_INVALID_FOREMAN';
    } else if (errorMessage.includes('fk_movements_projects')) {
      return 'MOVEMENT_CREATION_FAILED_INVALID_PROJECT';
    } else if (errorMessage.includes('fk_movement_hardwares_hardwares')) {
      return 'MOVEMENT_HARDWARE_CREATION_FAILED_INVALID_HARDWARE';
    }
  }

  // Default fallback
  return 'INTERNAL_SERVER_ERROR';
}
